/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";

import { getColorPalette } from "@/helpers/chartHelper";
import { formatNumber } from "@/utils/number";

import {
  Cell,
  Label,
  Legend,
  LegendProps,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip
} from "recharts";
import { twMerge } from "tailwind-merge";

const RADIAN = Math.PI / 180;
// Pie sweep (matches the <Pie startAngle/endAngle> below — full clockwise circle)
const START_ANGLE = 90;
const END_ANGLE = -270;
// Horizontal distance the anchor point is projected out from the pie edge
const ANCHOR_OFFSET = 36;
// Minimum vertical space each label block (name + value) occupies, in px
const LABEL_BLOCK_GAP = 36;
// Vertical padding kept from the top / bottom of the plotting area
const VERTICAL_PADDING = 8;
// Space reserved at the bottom for the legend
const LEGEND_RESERVE = 140;
// How far outside the donut edge the elbow (bend) sits
const ELBOW_GAP = 12;
const EDGE_PADDING = 18;
// Width of the label text box (name / value), in px
const LABEL_WIDTH = 80;
// Wider box used when the chart is roomy enough to show full, untruncated labels
const LABEL_WIDTH_FULL = 200;
// Below this chart width labels are truncated; at/above it they show in full
const LABEL_TRUNCATE_WIDTH = 520;
// Neutral gray used for leader lines, end dots, and the percentage text
const LEADER_COLOR = "#9CA3AF";
// Above this slice count the outer labels are hidden (legend/tooltip cover them)
const MAX_LABELS = 10;

type LabelPosition = { y: number; isRight: boolean };

/**
 * Computes a non-overlapping vertical position for every slice's label.
 *
 * Each label's ideal y is derived from its slice mid-angle, then labels on the
 * same side are spread apart so adjacent ones never stack. When one side runs
 * out of vertical room (small charts), its most vertically-neutral labels are
 * moved to the side that still has free space. Every label stays visible.
 */
const computeLabelPositions = (
  data: DonutChartItem[],
  totalValue: number,
  cy: number,
  outerRadius: number,
  chartHeight: number
): LabelPosition[] => {
  const sweep = END_ANGLE - START_ANGLE;
  let cumulative = 0;

  // Only lay out the slices that actually render a label (the first
  // MAX_LABELS). The trailing slices still shift each labeled slice's mid-angle
  // via totalValue, but they must NOT consume vertical slots in the spread —
  // otherwise the visible labels get compressed differently for 10 vs 20
  // slices even though the same 10 are drawn.
  const positions: (LabelPosition & { idealY: number; cos: number })[] = data
    .slice(0, MAX_LABELS)
    .map((item) => {
      const midValue = cumulative + item.value / 2;
      cumulative += item.value;
      const midAngle = START_ANGLE + (midValue / totalValue) * sweep;
      const sin = Math.sin(-RADIAN * midAngle);
      const cos = Math.cos(-RADIAN * midAngle);
      const idealY = cy + (outerRadius + ANCHOR_OFFSET) * sin;
      return { idealY, y: idealY, isRight: cos >= 0, cos };
    });

  const topBound = VERTICAL_PADDING;
  const bottomBound = Math.max(
    topBound + LABEL_BLOCK_GAP,
    chartHeight - LEGEND_RESERVE
  );
  // How many labels fit on one side given the available vertical space.
  const slotsPerSide = Math.max(
    1,
    Math.floor((bottomBound - topBound) / LABEL_BLOCK_GAP)
  );

  // Relieve an overcrowded side by moving its most vertically-neutral labels
  // (smallest |cos| — near the top/bottom, so re-routing them won't cross the
  // donut) onto the side that still has free slots.
  const rebalance = () => {
    const rightCount = positions.filter((p) => p.isRight).length;
    const leftCount = positions.length - rightCount;

    const overflowingRight =
      rightCount > slotsPerSide && leftCount < slotsPerSide;
    const overflowingLeft =
      leftCount > slotsPerSide && rightCount < slotsPerSide;
    if (!overflowingRight && !overflowingLeft) return;

    const fromRight = overflowingRight;
    const donorCount = fromRight ? rightCount : leftCount;
    const receiverCount = fromRight ? leftCount : rightCount;
    const moveCount = Math.min(
      donorCount - slotsPerSide,
      slotsPerSide - receiverCount
    );
    if (moveCount <= 0) return;

    positions
      .filter((p) => p.isRight === fromRight)
      .sort((a, b) => Math.abs(a.cos) - Math.abs(b.cos))
      .slice(0, moveCount)
      .forEach((p) => {
        p.isRight = !fromRight;
      });
  };

  rebalance();

  // Spread one side (indices into `positions`) so labels keep LABEL_BLOCK_GAP apart.
  const spreadSide = (indices: number[]) => {
    if (!indices.length) return;
    indices.sort((a, b) => positions[a].idealY - positions[b].idealY);

    // Pass 1 — push overlapping labels downward
    for (let i = 1; i < indices.length; i++) {
      const prev = positions[indices[i - 1]];
      const curr = positions[indices[i]];
      if (curr.y - prev.y < LABEL_BLOCK_GAP) curr.y = prev.y + LABEL_BLOCK_GAP;
    }

    // Pass 2 — if the block ran past the bottom, pull labels back up
    const last = positions[indices[indices.length - 1]];
    if (last.y > bottomBound) last.y = bottomBound;
    for (let i = indices.length - 2; i >= 0; i--) {
      const curr = positions[indices[i]];
      const next = positions[indices[i + 1]];
      if (next.y - curr.y < LABEL_BLOCK_GAP) curr.y = next.y - LABEL_BLOCK_GAP;
    }

    // Pass 3 — clamp the top and re-spread downward if needed
    const first = positions[indices[0]];
    if (first.y < topBound) {
      first.y = topBound;
      for (let i = 1; i < indices.length; i++) {
        const prev = positions[indices[i - 1]];
        const curr = positions[indices[i]];
        if (curr.y - prev.y < LABEL_BLOCK_GAP)
          curr.y = prev.y + LABEL_BLOCK_GAP;
      }
    }
  };

  spreadSide(positions.map((_, i) => i).filter((i) => positions[i].isRight));
  spreadSide(positions.map((_, i) => i).filter((i) => !positions[i].isRight));

  return positions.map(({ y, isRight }) => ({ y, isRight }));
};

type LabelLayoutCache = {
  signature: string;
  positions: LabelPosition[];
};

// Custom label for outside
const renderCustomizedLabel = (
  label: any,
  chartWidth: number,
  chartHeight: number,
  data: DonutChartItem[],
  totalValue: number,
  cache: { current: LabelLayoutCache | null }
) => {
  const { cx, cy, midAngle, outerRadius, fill, value, name, index } = label;

  // Only render labels for the first MAX_LABELS slices — beyond that, leader
  // lines and text would overlap and clutter the chart.
  if (index >= MAX_LABELS) return null;

  // Positions depend on the whole data set + geometry, which is identical for
  // every slice in a render — compute once and reuse across the label calls.
  // The per-slice shape must be in the key: layout is derived from cumulative
  // values, so two sets with the same length/total but different distribution
  // must not share a cached layout.
  const shape = data.map((d) => `${d.name}:${d.value}`).join(",");
  const signature = `${chartWidth}|${chartHeight}|${cx}|${cy}|${outerRadius}|${shape}`;
  if (cache.current?.signature !== signature) {
    cache.current = {
      signature,
      positions: computeLabelPositions(
        data,
        totalValue,
        cy,
        outerRadius,
        chartHeight
      )
    };
  }

  const position = cache.current.positions[index];
  if (!position) return null;

  const percentValue = (Number(value) / totalValue) * 100;

  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const { isRight, y: ey } = position;

  // Leader line: two segments only — a diagonal from the slice edge out to the
  // elbow (at label height), then a horizontal run to the chart edge.
  const sx = cx + (outerRadius - 4) * cos;
  const sy = cy + (outerRadius - 4) * sin;
  const ex = isRight ? chartWidth - EDGE_PADDING : EDGE_PADDING;
  const bx = isRight
    ? cx + outerRadius + ELBOW_GAP
    : cx - outerRadius - ELBOW_GAP;

  // Narrow charts truncate labels to a fixed box; roomy charts show them in
  // full using a wider box + no-wrap so the text is never cut off.
  const shouldTruncate = chartWidth < LABEL_TRUNCATE_WIDTH;
  const labelWidth = shouldTruncate ? LABEL_WIDTH : LABEL_WIDTH_FULL;
  const overflowClass = shouldTruncate ? "truncate" : "whitespace-nowrap";
  const exText = isRight ? ex - labelWidth : EDGE_PADDING;
  const alignClass = isRight ? "text-right" : "text-left";

  return (
    <g>
      <path
        d={`M${sx},${sy}L${bx},${ey}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={sx} cy={sy} r={2} fill={LEADER_COLOR} stroke="none" />
      <foreignObject x={exText} y={ey - 18} width={labelWidth} height={18}>
        <span
          className={twMerge(
            "block text-xs text-txtHeading",
            overflowClass,
            alignClass
          )}
          title={name}
        >
          {name}
        </span>
      </foreignObject>
      <foreignObject x={exText} y={ey + 2} width={labelWidth} height={18}>
        <span
          className={twMerge("block text-xs", overflowClass, alignClass)}
          style={{ color: LEADER_COLOR }}
        >
          {`${percentValue.toFixed(1)}%`}
        </span>
      </foreignObject>
    </g>
  );
};

// Center label with total
const renderCenterLabel = (content: any, data: any[]) => {
  const { viewBox } = content;
  const total = data.reduce((acc, cur) => acc + cur.value, 0);
  const totalStr = String(formatNumber(Number(total), "decimal"));
  let fontSize = 24;
  if (totalStr.length > 6) fontSize = 18;
  if (totalStr.length > 9) fontSize = 14;
  if (totalStr.length > 12) fontSize = 10;
  return (
    <text
      x={viewBox.cx}
      y={viewBox.cy}
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize={fontSize}
      fontWeight={700}
      fill="#000000E5"
    >
      {totalStr}
    </text>
  );
};

// Custom tooltip formatting
const CustomTooltip = ({ active, payload, totalValue }: any) => {
  if (active && payload?.length) {
    const { name, value } = payload[0];
    const percentValue = (value / totalValue) * 100;
    return (
      <div
        className={twMerge(
          "flex flex-col gap-2 bg-white border border-whisper rounded-[10px] p-3",
          "shadow-[0_2px_8px_rgba(0,0,0,0.08)] min-w-[170px]"
        )}
      >
        <span className="text-xs text-txtHeading font-normal">{name}</span>
        <span className="text-xs text-txtHeading font-semibold">
          {String(formatNumber(Number(value), "decimal"))} (
          {percentValue.toFixed(1)}%)
        </span>
      </div>
    );
  }
  return null;
};

const ScrollableLegend = (props: LegendProps): JSX.Element | null => {
  const { payload } = props;

  if (!payload) return null;

  return (
    <div
      className={twMerge(
        "legends max-h-32 overflow-y-auto flex flex-wrap gap-y-1 gap-x-2 flex-center custom-scrollbar",
        "w-full"
      )}
    >
      {payload.map((entry, index) => (
        <div
          key={`legend-item-${entry.value}-${index}`}
          className="flex items-center text-xs text-black text-opacity-70 whitespace-nowrap"
        >
          <span
            className="inline-block w-2 h-2 rounded-full mr-1"
            style={{
              background: entry.color,
              border: "1px solid #eee",
              flexShrink: 0
            }}
          />
          <span className="inline-block" title={String(entry.value)}>
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

export type DonutChartItem = { name: string; value: any };

type DonutChartProps = {
  data?: DonutChartItem[];
  height?: string | number;
};

/**
 * DonutChart component that dynamically generates enough colors for all slices.
 */
const DonutChart = ({ data = [], height }: DonutChartProps) => {
  const totalValue = data.reduce((acc, cur) => acc + cur.value, 0);
  // Generate as many colors as needed for this data set
  const COLORS = getColorPalette(data.length);
  const containerRef = useRef<HTMLDivElement>(null);
  const labelLayoutCache = useRef<LabelLayoutCache | null>(null);
  const numericHeight = typeof height === "number" ? height : 600;
  const [chartWidth, setChartWidth] = useState<number>(430);
  const [chartHeight, setChartHeight] = useState<number>(numericHeight);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    // Use ResizeObserver for accurate size updates
    const observer = new window.ResizeObserver(
      (entries: ResizeObserverEntry[]) => {
        for (const entry of entries) {
          setChartWidth(entry.contentRect.width || 430);
          setChartHeight(entry.contentRect.height || numericHeight);
        }
      }
    );
    observer.observe(node);
    // Call once for initial mount
    setChartWidth(node.offsetWidth || 430);
    setChartHeight(node.offsetHeight || numericHeight);

    return () => {
      observer.disconnect();
    };
  }, [numericHeight]);

  if (!data?.length || totalValue === 0)
    return (
      <div className="flex-center h-[400px] px-4">
        <p className="text-base text-txtHeading font-semibold text-center">
          No data available to display. Please check your data or try adjusting
          the configuration menu.
        </p>
      </div>
    );

  return (
    <div ref={containerRef} style={{ width: "100%", height: height || 600 }}>
      <ResponsiveContainer width="100%" height={height || 600}>
        <PieChart className="flex-center">
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            startAngle={90}
            endAngle={-270}
            innerRadius={50}
            outerRadius={100}
            paddingAngle={0}
            dataKey="value"
            labelLine={false}
            label={(label: any) =>
              renderCustomizedLabel(
                label,
                chartWidth,
                chartHeight,
                data,
                totalValue,
                labelLayoutCache
              )
            }
            isAnimationActive={false}
          >
            <Label
              content={(content) => renderCenterLabel(content, data)}
              position="center"
            />
            {data.map((entry, idx) => (
              <Cell
                className="outline-none"
                key={`cell-${entry.name}`}
                fill={COLORS[idx % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip
            content={(content) => (
              <CustomTooltip {...content} totalValue={totalValue} />
            )}
          />
          <Legend
            content={ScrollableLegend as any}
            layout="horizontal"
            verticalAlign="bottom"
            align="center"
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DonutChart;
